import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

export interface Notificatie {
  id: string;
  type: string;
  prioriteit?: string;
  titel: string;
  bericht: string;
  dossierId?: string;
  actionUrl?: string;
  gelezen: boolean;
  pushSent?: boolean;
  createdAt: string;
  userId: string;
}

interface NotificatieCount {
  ongelezen: number;
}

export function useNotificatieCount() {
  return useQuery({
    queryKey: ['notificaties', 'count'],
    queryFn: () => apiFetch<NotificatieCount>('/api/notificaties/count'),
    refetchInterval: 30_000,
  });
}

export function useNotificaties(params?: { ongelezen?: boolean; limit?: number; type?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.ongelezen) searchParams.set('ongelezen', 'true');
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.type) searchParams.set('type', params.type);
  const qs = searchParams.toString();

  return useQuery({
    queryKey: ['notificaties', 'lijst', params],
    queryFn: () => apiFetch<Notificatie[]>(`/api/notificaties${qs ? `?${qs}` : ''}`),
  });
}

export function useMarkeerGelezen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Notificatie>(`/api/notificaties/${id}/gelezen`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notificaties'] });
    },
  });
}

export function useMarkeerOngelezen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Notificatie>(`/api/notificaties/${id}/ongelezen`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notificaties'] });
    },
  });
}

export function useMarkeerAllesGelezen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ success: boolean; bijgewerkt: number }>('/api/notificaties/alles-gelezen', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notificaties'] });
    },
  });
}

export function useVapidKey() {
  return useQuery<{ publicKey: string }>({
    queryKey: ['notificaties', 'vapid-key'],
    queryFn: () => apiFetch('/api/notificaties/vapid-key'),
    staleTime: Infinity,
  });
}

export function useSubscribePush() {
  return useMutation({
    mutationFn: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      apiFetch('/api/notificaties/subscribe', {
        method: 'POST',
        body: JSON.stringify(subscription),
      }),
  });
}

export function useUnsubscribePush() {
  return useMutation({
    mutationFn: (endpoint: string) =>
      apiFetch('/api/notificaties/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),
  });
}
